import React, { createContext, useState, useContext, ReactNode } from "react";
import Lead from "app/apiService/lead/leadApiService";
import { APIResponseType } from 'app/apiService/types';

// ----------- Type Definitions -----------
interface LeadContextType {
  loading: boolean;
  error: string;
  success: boolean;
  leads: any[];
  currentPage: number;
  pageSize: number;

  setError: React.Dispatch<React.SetStateAction<string>>;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  
  addLead: (lead: any) => Promise<void>;
  deleteLead: (leadId: string) => Promise<void>;
  editLead: (leadId: string, data: any) => Promise<void>;
  getAllLeads: (params?: any) => Promise<void>;
}

// ----------- Context Create -----------
export const LeadContext = createContext<LeadContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const LeadProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [leads, setLeads] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ----------- Success Handler -----------
  const handleSuccess = (callback?: () => void) => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      if (callback) callback();
    }, 2000);
  };

  // ----------- API Calls -----------
  const addLead = async (lead: any, callback?: () => void) => {
    setLoading(true);
    setError("");
    try {
      await Lead.addLead(lead);
      handleSuccess(callback);
      await getAllLeads();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add lead");
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (leadId: string, callback?: () => void) => {
    setLoading(true);
    setError("");
    try {
      await Lead.deleteLead(leadId);
      handleSuccess(callback);
      await getAllLeads();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete lead");
    } finally {
      setLoading(false);
    }
  };

  const editLead = async (leadId: string, data: any, callback?: () => void) => {
    setLoading(true);
    setError("");
    try {
      await Lead.editLead(leadId, data);
      handleSuccess(callback);
      await getAllLeads();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to edit lead");
    } finally {
      setLoading(false);
    }
  };

  const getAllLeads = async (params?: any) => {
    setLoading(true);
    setError("");
    try {
      const response = await Lead.getAllLeadsForDataGrid(params);
      setLeads(response.body);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const value = {
    loading,
    error,
    success,
    leads,
    currentPage,
    pageSize,
    setError,
    setSuccess,
    setCurrentPage,
    setPageSize,
    addLead,
    deleteLead,
    editLead,
    getAllLeads,
  };

  return <LeadContext.Provider value={value}>{children}</LeadContext.Provider>;
};

// ----------- Custom Hook -----------
export const useLeads = () => {
  const context = useContext(LeadContext);
  if (context === undefined) {
    throw new Error('useLeads must be used within a LeadProvider');
  }
  return context;
}; 